import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { BadgeComponent } from '@epicuro-next/ataraxia/badge';
import {
  DropdownDirective,
  DropdownModule,
} from '@epicuro-next/ataraxia/dropdown';
import { IconsModule } from '@epicuro-next/ataraxia/icons';
import { dynamicElementsFilterSelect } from '@epicuro-next/constants/dynamic-elements';
import { Filter } from '@epicuro-next/models/filters';
import { DynamicComponentRegister } from '@epicuro-next/platform/dynamic-components-registrator';
import { FilterValue } from 'graphql-api/baskets-api-interfaces';

const defaultShowAllLabel = $localize`:Ataraxia|Ataraxia Filter Show All label@@ataraxiaShowAllFilterLabel:Show all`;

@DynamicComponentRegister(dynamicElementsFilterSelect, true)
@Component({
  selector: 'atx-filter-select-item',
  templateUrl: './filter-select-item.component.html',
  styleUrls: ['./filter-select-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DropdownModule, BadgeComponent, IconsModule, NgClass],
})
export class FilterSelectItemComponent {
  @Input() public filterItem!: Filter;

  @Input() public activeValues: string[] = [];

  @Input() public disabled = false;

  @Input() public selectSingleValueByDefault = false;

  @Input() public allowShowAll = true;

  @Input() public showAllForSingleValue = false;

  @Input() public showAllLabel = defaultShowAllLabel;

  @Input() public selectedValuesAsLabel = false;

  @Input() public dropdownButtonClass: string | null = null;

  @Input() public fieldDisplayName?: string = undefined;

  @Input() public labelAppend?: string = undefined;

  @Input() public showCountBadge = true;

  @Output() public toggleFilter = new EventEmitter<string[] | null>();

  @ViewChild(DropdownDirective)
  private readonly atxDropdownMenu?: DropdownDirective;

  public trackByValue(_: number, item: FilterValue) {
    return item.value;
  }

  public isSelected(value: string) {
    return this.activeValues.some((facetValue) => facetValue === value);
  }
  public isSelectedByDefault() {
    return (
      this.selectSingleValueByDefault &&
      this.activeValues.length === 0 &&
      this.filterItem?.values?.length === 1
    );
  }

  public get labelText(): string {
    const label =
      this.fieldDisplayName ??
      this.filterItem.fieldDisplayName ??
      this.filterItem.id;

    if (!this.selectedValuesAsLabel || this.activeValues.length === 0) {
      return (
        this.fieldDisplayName ??
        this.filterItem.fieldDisplayName ??
        this.filterItem.id
      );
    }

    return (
      this.filterItem.values
        .filter((item) => this.activeValues.indexOf(item.name) > -1)
        .map(
          (item) => `${item.displayName || item.name}${this.labelAppend ?? ''}`,
        )
        .join(', ') ?? label
    );
  }

  public get labelTextClass(): string | undefined {
    if (this.selectedValuesAsLabel && this.filterItem.isSingle) {
      return this.filterItem.values?.filter(
        (item) => this.activeValues.indexOf(item.name) > -1,
      )?.[0]?.classNames;
    }

    return undefined;
  }

  public onToggle(value: string | null) {
    if (!value) {
      this.toggleFilter.emit(null);
      this.atxDropdownMenu?.close();
      return;
    }

    const wasSelected = this.isSelected(value);

    if (
      this.selectSingleValueByDefault &&
      (this.filterItem.values?.length === 1 ||
        (!this.allowShowAll && wasSelected))
    ) {
      return;
    }

    if (this.filterItem.isSingle) {
      this.toggleFilter.emit(wasSelected ? null : [value]);
      return;
    }

    const values = wasSelected
      ? this.activeValues?.filter((facetValue) => facetValue !== value)
      : (this.activeValues ?? []).concat(value);

    this.toggleFilter.emit(values);
  }

  public get showAllVisible() {
    if (
      this.showAllForSingleValue &&
      (this.filterItem?.values ?? []).length === 1
    ) {
      return true;
    }
    return (this.filterItem?.values ?? []).length > 1 && this.allowShowAll;
  }
}
